import { of, EMPTY } from "rxjs";
import { switchMap } from "rxjs/operators";

import { ofHubEventPrefix } from "@services/hub";

export const START_SESSION = "pss/smartBridge/START_SESSION";
export const CLIENT_CONNECTED = "pss/smartBridge/CLIENT_CONNECTED";
export const CLIENT_DISCONNECTED = "pss/smartBridge/CLIENT_DISCONNECTED";
export const JOB_STATUS_CHANGED = "pss/smartBridge/JOB_STATUS_CHANGED";
export const JOB_UNHANDLED_EXCEPTION =
  "pss/smartBridge/JOB_UNHANDLED_EXCEPTION";
export const JOB_COMPLETE = "pss/smartBridge/JOB_COMPLETE";
export const CLEAR_LOG = "pss/smartBridge/CLEAR_LOG";
export const RESET = "pss/smartBridge/RESET";

let lastSlotId = -1;
export const getLastSlotId = () => lastSlotId;
export const setLastSlotId = (id) => (lastSlotId = id);

const initialState = {
  clientConnected: false,
  clientProducing: false,
  latestResult: null,
  progress: null,
  sessionId: null,
  disconnectReason: null,
  log: [],
};

function addLog(log, id, payload) {
  return [
    ...log,
    {
      id,
      number: log.length + 1,
      timestamp: Date.now(),
      taskName: payload.taskName,
      status: payload.status,
      progress: {
        messageKey: payload.progressMessage,
        value: payload.progressPercent,
      },
    },
  ];
}

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case START_SESSION:
      return {
        ...state,
        clientConnected: false,
        clientProducing: false,
        sessionId: action.payload.sessionId,
      };

    case RESET:
      return initialState;

    case CLIENT_CONNECTED:
      return {
        ...state,
        clientConnected: true,
        clientProducing: false,
        progress: null,
        log: addLog(state.log, "ClientConnected", {}),
      };

    case CLIENT_DISCONNECTED:
      return {
        ...state,
        clientConnected: false,
        clientProducing: false,
        progress: null,
        sessionId: null,
        disconnectReason: action.payload.reason,
        log: addLog(state.log, "ClientDisconnected", {
          reason: action.payload.reason,
        }),
      };

    case JOB_STATUS_CHANGED:
      return {
        ...state,
        clientProducing: true,
        progress: {
          messageKey: action.payload.progressMessage,
          value: action.payload.progressPercent,
        },
        log: addLog(state.log, "JobStatusChanged", action.payload),
      };

    case JOB_UNHANDLED_EXCEPTION:
      return {
        ...state,
        clientProducing: false,
        latestResult: {
          success: false,
          error: action.payload.message,
        },
        progress: null,
        log: addLog(state.log, "JobUnhandledException", {
          progressMessage: action.payload,
        }),
      };

    case JOB_COMPLETE:
      return {
        ...state,
        clientProducing: false,
        latestResult: action.payload,
        progress: {
          messageKey: "Complete",
          value: 100,
        },
        log: addLog(state.log, "JobComplete", {
          progressMessage: "Complete",
          progressPercent: 100,
        }),
      };

    case CLEAR_LOG:
      return {
        ...state,
        progress: null,
        log: [],
      };

    default:
      return state;
  }
}

export function startSession(sessionId) {
  return { type: START_SESSION, payload: { sessionId } };
}
export function clientConnected(sessionId) {
  return { type: CLIENT_CONNECTED, payload: { sessionId } };
}
export function clientDisconnected(sessionId, reason) {
  return { type: CLIENT_DISCONNECTED, payload: { sessionId, reason } };
}
export function jobStatusChanged(
  sessionId,
  { taskName, status, progressMessage, progressPercent }
) {
  return {
    type: JOB_STATUS_CHANGED,
    payload: { sessionId, taskName, status, progressMessage, progressPercent },
  };
}
export function jobUnhandledException(sessionId, message) {
  return { type: JOB_UNHANDLED_EXCEPTION, payload: { sessionId, message } };
}
export function jobComplete(sessionId, result) {
  return { type: JOB_COMPLETE, payload: sessionId, result };
}
export function clearLog() {
  return { type: CLEAR_LOG, payload: {} };
}
export function smartBridgeReset() {
  return { type: RESET };
}

export function smartBridgeLogEpic(action$, store$) {
  return action$.pipe(
    ofHubEventPrefix("SmartBridge."),
    switchMap(({ payload }) => {
      const { sessionId } = store$.value.smartBridge;
      if (!payload.data.sessionId || payload.data.sessionId !== sessionId)
        return EMPTY;

      switch (payload.id) {
        case "SmartBridge.ReceiveClientConnected":
          return of(clientConnected(payload.data.sessionId));

        case "SmartBridge.ReceiveClientDisconnected":
          return of(clientDisconnected(payload.data.sessionId));

        case "SmartBridge.ReceiveJobTaskStatusChanged":
          return of(jobStatusChanged(payload.data.sessionId, payload.data));

        case "SmartBridge.ReceiveJobComplete":
          return of(jobComplete(payload.data.sessionId, payload.data));

        case "SmartBridge.ReceiveUnhandledException":
          return of(
            jobUnhandledException(payload.data.sessionId, payload.data.message)
          );

        default:
          return EMPTY;
      }
    })
  );
}

export const smartBridgeEpics = [smartBridgeLogEpic];
